
import { useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ScheduledMatch, PublishMatchData } from '@/types/scheduledMatch';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeUserId } from '@/utils/uuidUtils';
import { createICSFile, addToCalendarOptimized, isIOS } from '@/utils/calendarUtils';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useEffect } from 'react';

const SCHEDULED_MATCHES_KEY = 'scheduled-matches';
const REFETCH_DEBOUNCE_MS = 400;

interface MatchLimitBlockedPlayer {
  user_id: string;
  name?: string;
  match_count?: number;
  has_active_premium?: boolean;
}

const formatBlockedPlayerNames = (players: MatchLimitBlockedPlayer[]) => {
  const names = players.map(player => player.name || 'A player');
  if (names.length <= 1) return names[0] || 'A player';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const getMatchLimitToastCopy = (body: any, currentUserId?: string) => {
  const blockedPlayers = Array.isArray(body?.blockedPlayers)
    ? (body.blockedPlayers as MatchLimitBlockedPlayer[])
    : [];

  if (blockedPlayers.length === 0) {
    return {
      title: 'Free-tier limit reached',
      description: body?.message || 'One of the players has hit the 50-match free-tier limit. Upgrade to Premium to continue.',
    };
  }

  const currentUserBlocked = currentUserId
    ? blockedPlayers.some(player => player.user_id === currentUserId)
    : false;

  if (currentUserBlocked) {
    return {
      title: 'Your free match limit is reached',
      description: body?.message || 'You have reached the 50-match free-tier limit. Upgrade to Premium to capture this score.',
    };
  }

  const names = formatBlockedPlayerNames(blockedPlayers);
  return {
    title: 'Another player hit the free limit',
    description: body?.message || `${names} ${blockedPlayers.length === 1 ? 'has' : 'have'} reached the 50-match free-tier limit and need Premium before this score can be captured.`,
  };
};

/** Insert a notification row for a single user (fire-and-forget) */
async function insertNotification(
  userId: string,
  type: string,
  title: string,
  message: string,
  content: Record<string, unknown>
) {
  await supabase.from('notifications').insert([{
    user_id: userId,
    type,
    title,
    message,
    content: content as any,
    is_read: false,
  }]);
}

const fetchScheduledMatchesQuery = async (userId: string): Promise<ScheduledMatch[]> => {
  const now = new Date();
  const nowIso = now.toISOString();
  const scoreCaptureWindowStart = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('published_matches')
    .select(`
      *,
      team1_player1_profile:profiles!published_matches_team1_player1_fkey(id, full_name, skill_rating, avatar_url),
      team1_player2_profile:profiles!published_matches_team1_player2_fkey(id, full_name, skill_rating, avatar_url),
      team2_player1_profile:profiles!published_matches_team2_player1_fkey(id, full_name, skill_rating, avatar_url),
      team2_player2_profile:profiles!published_matches_team2_player2_fkey(id, full_name, skill_rating, avatar_url),
      group:groups(id, name),
      poll:group_availability_polls(id, title),
      padel_clubs(id, club_name, street_address, phone_number, playtomic_tenant_id, slug)
    `)
    .eq('status', 'scheduled')
    .or(`expires_at.gt.${nowIso},match_date.gte.${scoreCaptureWindowStart}`)
    .order('match_date', { ascending: true });

  if (error) throw error;

  const userMatches = (data || []).filter(match =>
    match.team1_player1 === userId ||
    match.team1_player2 === userId ||
    match.team2_player1 === userId ||
    match.team2_player2 === userId
  );

  return userMatches.map(match => ({
    ...match,
    status: match.status as 'scheduled' | 'in_progress' | 'completed',
    group_name: match.group?.name,
    poll_title: match.poll?.title,
    club: match.padel_clubs ? {
      id: match.padel_clubs.id,
      club_name: match.padel_clubs.club_name,
      street_address: match.padel_clubs.street_address,
      phone_number: match.padel_clubs.phone_number,
      playtomic_tenant_id: match.padel_clubs.playtomic_tenant_id,
      slug: match.padel_clubs.slug,
    } : null
  }));
};

export const useScheduledMatches = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { prefs } = useNotificationPreferences();
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: scheduledMatches = [], isLoading: loading } = useQuery({
    queryKey: [SCHEDULED_MATCHES_KEY, user?.id],
    queryFn: () => fetchScheduledMatchesQuery(user!.id),
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
  });

  const invalidateScheduledMatches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SCHEDULED_MATCHES_KEY, user?.id] });
  }, [queryClient, user?.id]);

  // Debounced invalidation for realtime events — prevents thundering herd
  const debouncedInvalidate = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      invalidateScheduledMatches();
    }, REFETCH_DEBOUNCE_MS);
  }, [invalidateScheduledMatches]);

  // Realtime subscription — debounced to batch rapid changes
  useEffect(() => {
    if (!user) return;

    const channelName = `scheduled-matches-${user.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let channel: any = null;

    try {
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'published_matches' },
          () => {
            debouncedInvalidate();
          }
        );

      channel.subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          // subscribed
        } else if (status === 'CHANNEL_ERROR') {
          console.warn(`⚠️ ScheduledMatches: Channel error for ${channelName}`);
        }
      });
    } catch (error) {
      console.error('🚫 ScheduledMatches: Error setting up real-time subscription:', error);
    }

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.warn('⚠️ ScheduledMatches: Error removing channel:', error);
        }
      }
    };
  }, [user, debouncedInvalidate]);

  const publishMatch = async (matchData: PublishMatchData, deleteMatchIds?: string[]) => {
    try {
      const sanitizedMatchData = {
        suggested_match_id: sanitizeUserId(matchData.suggested_match_id),
        poll_id: sanitizeUserId(matchData.poll_id),
        group_id: sanitizeUserId(matchData.group_id),
        team1_player1: sanitizeUserId(matchData.team1_player1),
        team1_player2: sanitizeUserId(matchData.team1_player2),
        team2_player1: sanitizeUserId(matchData.team2_player1),
        team2_player2: sanitizeUserId(matchData.team2_player2),
        published_by: sanitizeUserId(user?.id)
      };

      if (!sanitizedMatchData.published_by) {
        throw new Error('User must be authenticated to publish matches');
      }

      const hasAnyPlayer = sanitizedMatchData.team1_player1 || sanitizedMatchData.team1_player2 || 
                           sanitizedMatchData.team2_player1 || sanitizedMatchData.team2_player2;
      if (!hasAnyPlayer) {
        throw new Error('Match must have at least one player');
      }

      const isAnySlotEmpty = !sanitizedMatchData.team1_player1 || !sanitizedMatchData.team1_player2 || 
                             !sanitizedMatchData.team2_player1 || !sanitizedMatchData.team2_player2;

      if (deleteMatchIds && deleteMatchIds.length > 0 && sanitizedMatchData.poll_id) {
        // Verify the stale scheduled rows actually get removed before inserting a
        // replacement — otherwise a blocked delete (e.g. RLS) creates a duplicate.
        const { data: existingStale, error: staleError } = await supabase
          .from('published_matches')
          .select('id')
          .eq('poll_id', sanitizedMatchData.poll_id)
          .eq('status', 'scheduled')
          .in('suggested_match_id', deleteMatchIds);

        if (staleError) {
          console.error('Error checking existing scheduled matches:', staleError);
        }

        const { data: deletedRows, error: deleteError } = await supabase
          .from('published_matches')
          .delete()
          .eq('poll_id', sanitizedMatchData.poll_id)
          .eq('status', 'scheduled')
          .in('suggested_match_id', deleteMatchIds)
          .select('id');

        if (deleteError) {
          console.error('Error deleting specific scheduled matches:', deleteError);
          toast({ title: "Error", description: "Could not remove the previously scheduled match. Nothing was changed.", variant: "destructive" });
          return false;
        }

        if ((existingStale?.length || 0) > (deletedRows?.length || 0)) {
          console.error('Stale scheduled match survived deletion', { existingStale, deletedRows });
          toast({ title: "Permission needed", description: "You don't have permission to replace the existing scheduled match. Ask a group admin to reschedule it.", variant: "destructive" });
          return false;
        }
      }


      const { data, error } = await supabase
        .from('published_matches')
        .insert({
          suggested_match_id: sanitizedMatchData.suggested_match_id,
          poll_id: sanitizedMatchData.poll_id,
          group_id: sanitizedMatchData.group_id,
          match_date: matchData.match_date,
          club_id: matchData.club_id,
          team1_player1: sanitizedMatchData.team1_player1,
          team1_player2: sanitizedMatchData.team1_player2,
          team2_player1: sanitizedMatchData.team2_player1,
          team2_player2: sanitizedMatchData.team2_player2,
          status: 'scheduled',
          published_by: sanitizedMatchData.published_by,
          is_open: isAnySlotEmpty,
          expires_at: matchData.match_date,
          challenge_match_id: matchData.challenge_match_id || null
        })
        .select()
        .single();

      if (error) throw error;

      // If this is linked to a challenge, update its status to 'scheduled' and notify players
      if (matchData.challenge_match_id) {
        await supabase
          .from('pair_challenge_matches')
          .update({ status: 'scheduled', updated_at: new Date().toISOString() })
          .eq('id', matchData.challenge_match_id);

        // Invalidate challenge cache so all group pages update
        queryClient.invalidateQueries({ queryKey: ['challenges'] });

        // Fetch challenge players for notifications
        const { data: challengeData } = await supabase
          .from('pair_challenge_matches')
          .select(`
            id, group_id, challenger_pair_id, challenged_pair_id,
            challenger_pair:group_pair_ladders!pair_challenge_matches_challenger_pair_id_fkey(player1_id, player2_id),
            challenged_pair:group_pair_ladders!pair_challenge_matches_challenged_pair_id_fkey(player1_id, player2_id)
          `)
          .eq('id', matchData.challenge_match_id)
          .single();

        if (challengeData) {
          const playerIds = [
            (challengeData.challenger_pair as any)?.player1_id,
            (challengeData.challenger_pair as any)?.player2_id,
            (challengeData.challenged_pair as any)?.player1_id,
            (challengeData.challenged_pair as any)?.player2_id,
          ].filter(Boolean) as string[];

          const matchDateStr = new Date(matchData.match_date).toLocaleString(undefined, {
            dateStyle: 'medium', timeStyle: 'short'
          });

          await Promise.all(
            playerIds.map(uid =>
              insertNotification(
                uid,
                'challenge_scheduled',
                'Challenge Match Scheduled! 📅',
                `Your challenge match has been scheduled for ${matchDateStr}.`,
                {
                  challenge_id: matchData.challenge_match_id,
                  group_id: matchData.group_id,
                  match_date: matchData.match_date,
                }
              )
            )
          );
        }
      }

      await handleCalendarIntegration({
        ...data,
        status: 'scheduled' as 'scheduled' | 'in_progress' | 'completed'
      });

      toast({ title: "Success", description: "Match scheduled successfully" });
      invalidateScheduledMatches();
      return true;
    } catch (err) {
      console.error('Error publishing match:', err);
      toast({ title: "Error", description: "Failed to schedule match", variant: "destructive" });
      return false;
    }
  };

  const captureScore = async (
    matchId: string,
    sets: any[],
    winnerTeam: number | null,
    onMilestoneCheck?: () => Promise<void>,
    onAchievementsAwarded?: (achievements: any[]) => void,
    swappedPlayers?: {
      team1_player1?: string;
      team1_player2?: string;
      team2_player1?: string;
      team2_player2?: string;
    },
    isCompetitive?: boolean
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke('migrate-published-match-to-real', {
        body: { publishedMatchId: matchId, sets, winnerTeam, swappedPlayers, isCompetitive: isCompetitive !== false }
      });

      // Try to extract structured error body from non-2xx responses
      if (error) {
        let body: any = null;
        try {
          const resp = (error as any)?.context;
          if (resp && typeof resp.json === 'function') {
            body = await resp.clone().json();
          }
        } catch { /* ignore */ }

        if (body?.error === 'match_limit_reached') {
          const copy = getMatchLimitToastCopy(body, user?.id);
          toast({
            title: copy.title,
            description: copy.description,
            variant: 'destructive',
          });
          return null;
        }
        throw new Error(body?.message || body?.error || error.message);
      }
      if (data?.error === 'match_limit_reached') {
        const copy = getMatchLimitToastCopy(data, user?.id);
        toast({
          title: copy.title,
          description: copy.description,
          variant: 'destructive',
        });
        return null;
      }
      if (data?.error) throw new Error(data.message || data.error);

      toast({ title: "Success", description: "Match score captured successfully" });
      invalidateScheduledMatches();

      if (data?.awardedAchievements && data.awardedAchievements.length > 0 && onAchievementsAwarded) {
        onAchievementsAwarded(data.awardedAchievements);
      }

      if (onMilestoneCheck) {
        setTimeout(() => { onMilestoneCheck(); }, 1000);
      }

      return data.matchId;
    } catch (err: any) {
      console.error('[captureScore] Error capturing score:', err);
      toast({ title: "Error", description: err?.message || 'Failed to capture match score', variant: "destructive" });
      return null;
    }
  };

  const deleteScheduledMatch = async (matchId: string) => {
    if (!user) return;

    try {
      const { data: matchData, error: fetchError } = await supabase
        .from('published_matches')
        .select(`
          *,
          team1_player1_profile:profiles!published_matches_team1_player1_fkey(id, full_name),
          team1_player2_profile:profiles!published_matches_team1_player2_fkey(id, full_name),
          team2_player1_profile:profiles!published_matches_team2_player1_fkey(id, full_name),
          team2_player2_profile:profiles!published_matches_team2_player2_fkey(id, full_name)
        `)
        .eq('id', matchId)
        .eq('published_by', user.id)
        .single();

      if (fetchError || !matchData) {
        throw new Error('Match not found or you do not have permission to delete it');
      }

      const { error: deleteError } = await supabase
        .from('published_matches')
        .delete()
        .eq('id', matchId)
        .eq('published_by', user.id);

      if (deleteError) throw deleteError;

      // If this was linked to a challenge, cancel it and notify all players
      if (matchData.challenge_match_id) {
        await supabase
          .from('pair_challenge_matches')
          .update({ status: 'cancelled', updated_at: new Date().toISOString() })
          .eq('id', matchData.challenge_match_id);

        // Invalidate challenge cache
        queryClient.invalidateQueries({ queryKey: ['challenges'] });

        // Fetch challenge players for notifications
        const { data: challengeData } = await supabase
          .from('pair_challenge_matches')
          .select(`
            id, group_id, challenger_pair_id, challenged_pair_id,
            challenger_pair:group_pair_ladders!pair_challenge_matches_challenger_pair_id_fkey(player1_id, player2_id),
            challenged_pair:group_pair_ladders!pair_challenge_matches_challenged_pair_id_fkey(player1_id, player2_id)
          `)
          .eq('id', matchData.challenge_match_id)
          .single();

        if (challengeData) {
          const playerIds = [
            (challengeData.challenger_pair as any)?.player1_id,
            (challengeData.challenger_pair as any)?.player2_id,
            (challengeData.challenged_pair as any)?.player1_id,
            (challengeData.challenged_pair as any)?.player2_id,
          ].filter(Boolean) as string[];

          await Promise.all(
            playerIds.map(uid =>
              insertNotification(
                uid,
                'challenge_cancelled',
                'Challenge Cancelled ⚠️',
                'The scheduled challenge match was deleted. The linked challenge has been cancelled.',
                {
                  challenge_id: matchData.challenge_match_id,
                  group_id: challengeData.group_id,
                }
              )
            )
          );
        }
      }

      invalidateScheduledMatches();
      return { success: true, challengeCancelled: !!matchData.challenge_match_id };
    } catch (error) {
      console.error('Error deleting scheduled match:', error);
      throw error;
    }
  };

  const handleCalendarIntegration = async (publishedMatch: ScheduledMatch) => {
    try {
      const calendarPreference = (prefs as any)?.calendar_integration || 'ask';

      if (calendarPreference === 'disabled') return;

      const isParticipant = [
        publishedMatch.team1_player1,
        publishedMatch.team1_player2,
        publishedMatch.team2_player1,
        publishedMatch.team2_player2
      ].includes(user?.id);

      if (!isParticipant) return;

      const icsContent = await createICSFile(publishedMatch);

      if (calendarPreference === 'auto_download') {
        const result = await addToCalendarOptimized(icsContent, publishedMatch);
        toast({
          title: result.success ? "Calendar Event Added" : "Calendar Integration Issue",
          description: result.message,
          variant: result.success ? "default" : "destructive",
        });
      } else {
        toast({ title: "Add to Calendar?", description: "Your scheduled match is ready to be added to your calendar" });
      }
    } catch (error) {
      console.error('Error with calendar integration:', error);
      toast({ title: "Calendar Integration Failed", description: "Could not create calendar event for this match", variant: "destructive" });
    }
  };

  const generateCalendarFile = async (match: ScheduledMatch) => {
    try {
      const icsContent = await createICSFile(match);
      const result = await addToCalendarOptimized(icsContent, match);
      toast({
        title: result.success ? "Calendar Event Generated" : "Calendar Error",
        description: result.message,
        variant: result.success ? "default" : "destructive",
      });
    } catch (error) {
      console.error('Error generating calendar file:', error);
      toast({ title: "Calendar Error", description: "Could not create calendar event", variant: "destructive" });
    }
  };

  return {
    scheduledMatches,
    loading,
    fetchScheduledMatches: invalidateScheduledMatches,
    publishMatch,
    captureScore,
    deleteScheduledMatch,
    generateCalendarFile
  };
};
